/**
 * @file SearchComponent.ts
 * @module _admin/assets/ts/components/SearchComponent
 * @description Implements the SearchComponent interactive admin-shell component, including initialization, state management, and DOM event handling.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

import {
  EventHandlerUtil,
  DataUtil,
  getBreakpoint,
  getAttributeValueByBreakpoint,
  stringSnakeToCamel,
  getObjectPropertyValueByKey,
  getViewPort,
  isVisibleElement,
  throttle,
} from "../_utils/index";

import { MenuComponent, defaultMenuOptions } from "./MenuComponent";

export interface ISearchOptions {
  minLength: number; // Miniam text lenght to query search
  keypress: boolean; // Enable search on keypress
  enter: boolean; // Enable search on enter key press
  layout: "menu" | "inline"; // Use 'menu' or 'inline' layout options to display search results
  responsive?: number; // Pass integer value or bootstrap compatible breakpoint key(sm,md,lg,xl,xxl) to enable reponsive form mode for device width below the breakpoint value
  showOnFocus: boolean; // Always show menu on input focus
}

export interface ISearchQueries {
  componentName: string;
  instanseQuery: string;
  attrQuery: string;
}

const defaultSearchOptions: ISearchOptions = {
  minLength: 2, // Miniam text lenght to query search
  keypress: true, // Enable search on keypress
  enter: true, // Enable search on enter key press
  layout: "menu", // Use 'menu' or 'inline' layout options to display search results
  showOnFocus: true, // Always show menu on input focus
};

const defaultSearchQueires: ISearchQueries = {
  componentName: "search",
  instanseQuery: "[data-kt-search]",
  attrQuery: "data-kt-search-",
};

/**
 * @class SearchComponent
 * @description UI controller component for handling interactive search bars, dropdown menus, autocomplete suggestions, and input states in the admin web panel.
 */
class SearchComponent {
  element: HTMLElement;
  contentElement: HTMLElement;
  formElement: HTMLFormElement;
  inputElement: HTMLInputElement;
  spinnerElement: HTMLElement;
  clearElement: HTMLElement;
  toggleElement: HTMLElement;
  submitElement: HTMLElement;
  toolbarElement: HTMLElement;
  resultsElement: HTMLElement;
  suggestionElement: HTMLElement;
  emptyElement: HTMLElement;
  layout: any;

  options: ISearchOptions;
  queries: ISearchQueries;

  processing: boolean = false;
  menuObject: MenuComponent | undefined;

  /**
   * Creates an instance of SearchComponent.
   *
   * @param {HTMLElement} _element - Root DOM element containing the search component.
   * @param {ISearchOptions} _options - Configuration options for search behavior.
   * @param {ISearchQueries} _queries - Query selectors and attribute names for DOM mapping.
   */
  constructor(
    _element: HTMLElement,
    _options: ISearchOptions,
    _queries: ISearchQueries
  ) {
    // Variables
    this.options = Object.assign(defaultSearchOptions, _options);
    this.queries = _queries;

    // Elements
    this.element = _element;
    this.contentElement = this._getElement("content") as HTMLElement;
    this.formElement = this._getElement("form") as HTMLFormElement;
    this.inputElement = this._getElement("input") as HTMLInputElement;
    this.spinnerElement = this._getElement("spinner") as HTMLElement;
    this.clearElement = this._getElement("clear") as HTMLElement;
    this.toggleElement = this._getElement("toggle") as HTMLElement;
    this.submitElement = this._getElement("submit") as HTMLElement;
    this.toolbarElement = this._getElement("toolbar") as HTMLElement;

    this.resultsElement = this._getElement("results") as HTMLElement;
    this.suggestionElement = this._getElement("suggestion") as HTMLElement;
    this.emptyElement = this._getElement("empty") as HTMLElement;

    // Layout
    this.layout = this.getOption("layout");
    if (this.layout === "menu") {
      this.menuObject = new MenuComponent(
        this.contentElement,
        defaultMenuOptions
      );
    }

    // Update
    this.update();

    // Event Handlers
    this.handlers();

    DataUtil.set(this.element, this.queries.componentName, this);
  }

  /**
   * Finds a child element within the component matching a specific search element data attribute.
   *
   * @param {string} name - Element identifier suffix for data-kt-search-element.
   * @returns {HTMLElement|null} The matching DOM element or null.
   */
  private _getElement = (name: string) => {
    return this.element.querySelector(
      '[data-kt-search-element="' + name + '"]'
    );
  };

  // Get option
  /**
   * Retrieves a configuration option value from element attributes or fallback options.
   *
   * @param {string} name - Option name to retrieve.
   * @returns {any} The resolved option value.
   */
  private getOption = (name: string) => {
    const attr = this.element.getAttribute(`${this.queries.attrQuery}${name}`);
    if (attr) {
      let value = getAttributeValueByBreakpoint(attr);

      if (value !== null && String(value) === "true") {
        return true;
      } else if (value !== null && String(value) === "false") {
        return false;
      }

      return value;
    } else {
      const optionName = stringSnakeToCamel(name);

      const option = getObjectPropertyValueByKey(this.options, optionName);
      if (option) {
        return getAttributeValueByBreakpoint(option);
      } else {
        return null;
      }
    }
  };

  // Check if responsive form mode is enabled
  /**
   * Determines whether the responsive form layout mode should be enabled based on viewport width and breakpoints.
   *
   * @returns {string|null} "on", "off", or null if responsive is not configured.
   */
  private getResponsiveFormMode = () => {
    const responsive = this.getOption("responsive") as string;
    const width = getViewPort().width;

    if (!responsive) {
      return null;
    }

    const breakpoint = getBreakpoint(responsive);
    let breakPointNum = -1;
    if (!breakpoint) {
      breakPointNum = parseInt(responsive);
    } else {
      breakPointNum = +breakpoint;
    }

    if (width < breakPointNum) {
      return "on";
    } else {
      return "off";
    }
  };

  // Focus
  /**
   * Marks the search component as focused and conditionally displays the search results container.
   */
  private focus = () => {
    this.element.classList.add("focus");

    if (
      this.getOption("show-on-focus") === true ||
      this.inputElement.value.length >= this.options.minLength
    ) {
      this.show();
    }
  };

  // Blur
  /**
   * Removes the focus visual state from the search component element.
   */
  private blur = () => {
    this.element.classList.remove("focus");
  };

  // Enter
  /**
   * Handles keypress events on the search input, triggering a search when the Enter key is pressed.
   *
   * @param {KeyboardEvent} e - Keyboard event object.
   */
  private enter = (e: KeyboardEvent) => {
    const key = e.charCode || e.keyCode || 0;

    if (key === 13) {
      e.preventDefault();

      this.search();
    }
  };

  // Input
  /**
   * Handles input change events, initiating a search if the input length reaches the minimum threshold or clearing if empty.
   */
  private input = () => {
    if (this.getOption("min-length")) {
      const minLength = parseInt(this.getOption("min-length") as string);

      if (this.inputElement.value.length >= minLength) {
        this.search();
      } else if (this.inputElement.value.length === 0) {
        this.clear();
      }
    }
  };

  /**
   * Attaches event listeners for input focus, blur, typing, submission, clearing, menu toggling, and window resizing.
   */
  private handlers(): void {
    const context = this;

    // Focus
    this.inputElement.addEventListener("focus", this.focus);

    // Blur
    this.inputElement.addEventListener("blur", this.blur);

    // Keypress
    if (this.getOption("keypress") === true) {
      this.inputElement.addEventListener("input", this.input);
    }

    // Submit
    if (this.submitElement) {
      this.submitElement.addEventListener("click", this.search);
    }

    // Enter
    if (this.getOption("enter") === true) {
      this.inputElement.addEventListener("keypress", this.enter);
    }

    // Clear
    if (this.clearElement) {
      this.clearElement.addEventListener("click", this.clear);
    }

    // Menu
    if (this.menuObject) {
      // Toggle menu
      if (this.toggleElement) {
        this.toggleElement.addEventListener("click", this.show);

        this.menuObject.on("kt.menu.dropdown.show", function () {
          // @ts-ignore
          if (isVisibleElement(context.toggleElement)) {
            // @ts-ignore
            context.toggleElement.classList.add("active");
            // @ts-ignore
            context.toggleElement.classList.add("show");
          }
        });

        this.menuObject.on("kt.menu.dropdown.hide", function () {
          // @ts-ignore
          if (isVisibleElement(context.toggleElement)) {
            // @ts-ignore
            context.toggleElement.classList.remove("active");
            // @ts-ignore
            context.toggleElement.classList.remove("show");
          }
        });
      }

      this.menuObject.on("kt.menu.dropdown.shown", function () {
        // @ts-ignore
        context.inputElement.focus();
      });
    }

    // Window resize handling
    window.addEventListener("resize", () => {
      let timer;

      throttle(
        timer,
        () => {
          this.update();
        },
        200
      );
    });
  }

  ///////////////////////
  // ** Public API  ** //
  ///////////////////////
  // Update
  /**
   * Updates the component layout and adjusts responsive form positioning when operating within a menu mode.
   */
  public update = () => {
    // Handle responsive form
    if (this.layout === "menu") {
      let responsiveFormMode = this.getResponsiveFormMode();

      if (
        responsiveFormMode === "on" &&
        !this.contentElement.contains(this.formElement)
      ) {
        this.contentElement.prepend(this.formElement);
        this.formElement.classList.remove("d-none");
      } else if (
        responsiveFormMode === "off" &&
        this.contentElement.contains(this.formElement)
      ) {
        this.element.prepend(this.formElement);
        this.formElement.classList.add("d-none");
      }
    }
  };

  // Show menu
  /**
   * Displays the search menu container if a menu object is configured.
   */
  public show = () => {
    if (this.menuObject) {
      this.update();

      this.menuObject.show(this.element);
    }
  };

  // Hide menu
  /**
   * Hides the search menu container if a menu object is configured.
   */
  public hide = () => {
    if (this.menuObject) {
      this.update();

      this.menuObject.hide(this.element);
    }
  };

  // Search
  /**
   * Activates the search loading state by displaying the spinner, hiding clear and toolbar elements, and triggering process events.
   */
  public search = () => {
    if (!this.processing) {
      // Show search spinner
      if (this.spinnerElement) {
        this.spinnerElement.classList.remove("d-none");
      }

      // Hide search clear button
      if (this.clearElement) {
        this.clearElement.classList.add("d-none");
      }

      // Hide search toolbar
      if (this.toolbarElement) {
        this.toolbarElement.classList.add("d-none");
      }

      // Focus input
      this.inputElement.focus();

      this.processing = true;
      EventHandlerUtil.trigger(this.element, "kt.search.process", this);
    }
  };

  // Complete
  /**
   * Completes the search process by hiding the spinner, restoring toolbar buttons, and showing results.
   */
  public complete = () => {
    if (this.spinnerElement) {
      this.spinnerElement.classList.add("d-none");
    }

    // Show search toolbar
    if (this.clearElement) {
      this.clearElement.classList.remove("d-none");
    }

    if (this.inputElement.value.length === 0) {
      this.clear();
    }

    // Focus input
    this.inputElement.focus();

    this.show();

    this.processing = false;
  };

  // Clear
  /**
   * Clears the search input value, resets UI elements, and hides results or menu depending on configuration.
   */
  public clear = () => {
    if (EventHandlerUtil.trigger(this.element, "kt.search.clear") === false) {
      return;
    }

    // Clear and focus input
    this.inputElement.value = "";
    this.inputElement.focus();

    // Hide clear icon
    if (this.clearElement) {
      this.clearElement.classList.add("d-none");
    }

    // Show search toolbar
    if (this.toolbarElement) {
      this.toolbarElement.classList.remove("d-none");
    }

    // Hide menu
    if (this.getOption("show-on-focus") === false) {
      this.hide();
    }

    EventHandlerUtil.trigger(this.element, "kt.search.cleared");
  };

  /**
   * Checks whether a search request is currently being processed.
   *
   * @returns {boolean} True if processing, false otherwise.
   */
  public isProcessing = () => {
    return this.processing;
  };

  /**
   * Retrieves the current text value entered in the search input.
   *
   * @returns {string} Current search query string.
   */
  public getQuery = () => {
    return this.inputElement.value;
  };

  /**
   * Retrieves the associated menu component instance if configured.
   *
   * @returns {MenuComponent|undefined} The menu component instance or undefined.
   */
  public getMenu = () => {
    return this.menuObject;
  };

  /**
   * Retrieves the HTML form element used by the search component.
   *
   * @returns {HTMLFormElement} The search form element.
   */
  public getFormElement = () => {
    return this.formElement;
  };

  /**
   * Retrieves the HTML input element used for typing search queries.
   *
   * @returns {HTMLInputElement} The search input element.
   */
  public getInputElement(): HTMLInputElement {
    return this.inputElement;
  }

  /**
   * Retrieves the container element holding search content.
   *
   * @returns {HTMLElement} The content container element.
   */
  public getContentElement(): HTMLElement {
    return this.contentElement;
  }

  /**
   * Retrieves the root DOM element of the search component.
   *
   * @returns {HTMLElement} The root search element.
   */
  public getElement(): HTMLElement {
    return this.element;
  }

  // Event API
  /**
   * Attaches an event handler to the search element.
   *
   * @param {string} name - Event name.
   * @param {Function} handler - Callback function.
   * @returns {any} Event utility subscription result.
   */
  public on = (name: string, handler: Function) => {
    return EventHandlerUtil.on(this.element, name, handler);
  };

  /**
   * Attaches a one-time event handler to the search element.
   *
   * @param {string} name - Event name.
   * @param {Function} handler - Callback function.
   * @returns {any} Event utility subscription result.
   */
  public one = (name: string, handler: Function) => {
    return EventHandlerUtil.one(this.element, name, handler);
  };

  /**
   * Removes an event handler from the search element.
   *
   * @param {string} name - Event name.
   * @param {string} handlerId - Handler subscription identifier.
   * @returns {any} Event utility unsubscription result.
   */
  public off = (name: string, handlerId: string) => {
    return EventHandlerUtil.off(this.element, name, handlerId);
  };

  // Static methods
  /**
   * Retrieves an existing SearchComponent instance attached to a DOM element.
   *
   * @param {HTMLElement} el - Target DOM element.
   * @param {string} [componentName] - Component identifier key.
   * @returns {SearchComponent|null} The search component instance or null.
   */
  public static getInstance = (
    el: HTMLElement,
    componentName: string = defaultSearchQueires.componentName
  ) => {
    const Search = DataUtil.get(el, componentName);
    if (Search) {
      return Search as SearchComponent;
    }

    return null;
  };

  /**
   * Instantiates SearchComponent for all matching elements found in the document body.
   *
   * @param {string} [selector] - CSS selector for search elements.
   * @param {ISearchOptions} [options] - Search options configuration.
   * @param {ISearchQueries} [queries] - Query selectors configuration.
   */
  public static createInstances = (
    selector: string = defaultSearchQueires.instanseQuery,
    options: ISearchOptions = defaultSearchOptions,
    queries: ISearchQueries = defaultSearchQueires
  ) => {
    const elements = document.body.querySelectorAll(selector);
    elements.forEach((el) => {
      const item = el as HTMLElement;
      let Search = SearchComponent.getInstance(item);
      if (!Search) {
        Search = new SearchComponent(item, options, queries);
      }
    });
  };

  /**
   * Instantiates or retrieves a SearchComponent for a single matching element.
   *
   * @param {string} [selector] - CSS selector for the search element.
   * @param {ISearchOptions} [options] - Search options configuration.
   * @param {ISearchQueries} [queries] - Query selectors configuration.
   * @returns {SearchComponent|undefined} The search component instance or undefined.
   */
  public static createInsance = (
    selector: string = defaultSearchQueires.instanseQuery,
    options: ISearchOptions = defaultSearchOptions,
    queries: ISearchQueries = defaultSearchQueires
  ): SearchComponent | undefined => {
    const element = document.body.querySelector(selector);
    if (!element) {
      return;
    }
    const item = element as HTMLElement;
    let Search = SearchComponent.getInstance(item);
    if (!Search) {
      Search = new SearchComponent(item, options, queries);
    }
    return Search;
  };

  /**
   * Bootstraps search components across the document.
   *
   * @param {string} [selector] - CSS selector for search elements.
   */
  public static bootstrap = (
    selector: string = defaultSearchQueires.instanseQuery
  ) => {
    SearchComponent.createInstances(selector);
  };

  /**
   * Reinitializes search components across the document.
   *
   * @param {string} [selector] - CSS selector for search elements.
   */
  public static reinitialization = (
    selector: string = defaultSearchQueires.instanseQuery
  ) => {
    SearchComponent.createInstances(selector);
  };
}
export { SearchComponent, defaultSearchOptions, defaultSearchQueires };
